import { parseISO } from "date-fns";

interface DataPoint {
  x: Date
  /** Size of the client base at this point. */
  y: number
  /** Arrivals in this bucket, carried for the tooltip. */
  delta: number
}

interface ApiDataEntry {
  period: string
  count: number
  cumulative?: number
}

interface ApiPeriod {
  start: string
  end: string
}

interface ApiData {
  granularity: string
  /**
   * Echo of the requested window. Absent on API versions before the
   * new-tenants endpoint started echoing it back.
   */
  period?: ApiPeriod
  /**
   * Tenants that already existed when the window opened. Absent on API
   * versions before the endpoint started reporting the running base.
   */
  baseline?: number
  total: number
  data: ApiDataEntry[]
}

interface Dataset {
  label: string
  data: DataPoint[]
  grouping: string
}

interface DataGroup {
  datasets: Dataset[]
}

interface ParsedData {
  datasets: Record<string, DataGroup>
}

function fillIfEmpty(
  array: DataPoint[],
  start: Date,
  end: Date,
  baseline: number,
): void {
  if (!array.length) {
    array.push({
      x: start,
      y: baseline,
      delta: 0,
    });

    array.push({
      x: end,
      y: baseline,
      delta: 0,
    });
  }
}

export function parseData(apiData: ApiData): ParsedData {
  const tenantData: DataPoint[] = [];
  const dataGroups: Record<string, DataGroup> = {
    new_tenants: {
      datasets: [
        {
          label: "Clientes",
          data: tenantData,
          grouping: apiData.granularity,
        },
      ],
    },
  };

  const period = apiData.period;
  const baseline = apiData.baseline ?? 0;

  const data = apiData.data;
  for (const entry of data) {
    const date = parseISO(entry.period);
    tenantData.push({
      x: date,
      // Falls back to the bucket's own arrivals on an API old enough not to
      // report the running base, so a rollback degrades instead of throwing.
      y: entry.cumulative ?? entry.count,
      delta: entry.count,
    });
  }

  for (const dataGroup in dataGroups) {
    for (const dataset of dataGroups[dataGroup].datasets) {
      // The series arrives dense, so this only fires when the window itself
      // came back empty — and only when we know the window at all.
      if (period) {
        fillIfEmpty(
          dataset.data,
          parseISO(period.start),
          parseISO(period.end),
          baseline,
        );
      }
      dataset.grouping = apiData.granularity;
    }
  }

  return {
    datasets: dataGroups,
  };
}
